/**
 * kindLabel — maps raw Langfuse trace names to human-friendly labels for the
 * "Kind" column in every trace table / drill-down.
 *
 * Raw names are fine for engineers but noisy for operators: `sales-coach-
 * request` and `scheduled_brief` don't read as English. This mapping is the
 * single source of truth so Overview / Quality / Operations / Time-saved
 * modals all say the same thing for the same event.
 *
 * The full list of observed `name` values (from langfuse_traces, 2026-04-24):
 *   sales-coach-request, scheduled_brief, qa-scorer:pass1..3, jira_prefill_
 *   click, circuit_tripped, brief_command, article:revise, article:create,
 *   article:publish, kb_sync, morning-brief, search-agent, kb-covers,
 *   jira-enrichment(-v2), `` (empty).
 *
 * We lean into short labels (<= 22 chars) because the Kind column is narrow
 * (~140px desktop). Tooltips surface the full raw name for the rare case an
 * operator needs to grep against production logs.
 */

const KIND_LABELS: Record<string, string> = {
  'sales-coach-request': 'Chat message',
  // The web coach's turn. Same population as the row above — one user, one
  // message, one pipeline — so the label says so rather than falling through
  // `titleCase` to "Coach Web Request", which reads as a different kind of
  // event beside "Chat message" in the same latency drill-down.
  'coach-web-request': 'Chat message (web)',
  // An errand one RM asked the coach to run for somebody at a named moment, RUN for its
  // recipient (`services/assignments/turn.ts`). `titleCase` would render it "Coach
  // Assignment", which reads as plausible English for a thing nobody on the team calls
  // that — the fallback's failure mode here is not looking unknown, it is looking known.
  //
  // ⚠️ IT SAID «Errand delivered», WHICH THE ROW CANNOT PROMISE. The label is on the
  // TRACE, and the trace is opened when the attempt BEGINS (`startTrace` in the tick,
  // before the turn runs). All four endings write the same trace name: `delivered`,
  // `waiting_auth` (the words landed, the work did not — he has no connection),
  // `failed_turn` (the answer was never composed) and `failed_post` (Slack refused it).
  // So an errand that reached nobody read, to whoever opened the dashboard, as one that
  // arrived — and «did an errand actually reach a person» is the first question anyone
  // asks of this feature.
  //
  // WHY A NAME FOR THE ATTEMPT IS THE TRUTHFUL CEILING HERE, rather than a label that
  // reads the outcome. The outcome IS recorded — `endTrace` writes one enumerated word
  // to `metadata.outcome` at each of the four endings — but nothing on this surface can
  // see it: it is not a hot column in the mirror (`dashboard/langfuse/sync.ts`), so it
  // arrives inside `raw_metadata`; no query selects it, `TraceRow` (`services/api-types.ts`)
  // has no field for it, and `kindLabel` is handed the NAME and nothing else. Reading
  // the outcome would mean a column, a query, an API field and a cell — a different
  // change from this one, and until it exists this label may not claim delivery.
  // `errorClass` on the same row already shows the two THROWN endings; `waiting_auth`
  // is the one that carries no error and looks like a success from every column.
  'coach-assignment': 'Errand run',
  // The author pressing «Тест» on his own card: the same errand run for real under his own
  // access and shown to him alone. Named as a rehearsal because that is why it is out of
  // every population (`DIAGNOSTIC_TRACE_NAMES`), and an operator who sees one in a raw
  // trace list should be able to tell it apart from the delivery at a glance.
  'coach-assignment-preview': 'Errand preview',
  'scheduled_brief': 'Morning brief',
  'onboarding_message': 'Onboarding',
  'mini_brief': 'Birthday mini-brief',
  'morning-brief': 'Morning brief',
  'brief_command': '/brief command',
  'jira_prefill_click': 'Jira prefill click',
  'jira-enrichment': 'Jira enrichment',
  'jira-enrichment-v2': 'Jira enrichment',
  'article:publish': 'KB article published',
  'article:create': 'KB article drafted',
  'article:revise': 'KB article revised',
  'circuit_tripped': 'Circuit tripped',
  'search-agent': 'Search agent',
  'kb-sync': 'KB sync',
  'kb_sync': 'KB sync',
  'kb-covers': 'KB covers',
  'qa-scorer:pass1': 'QA scorer (pass 1)',
  'qa-scorer:pass2': 'QA scorer (pass 2)',
  'qa-scorer:pass3': 'QA scorer (pass 3)',
};

/**
 * Trace names whose delivered Slack content is persisted in `brief_messages`
 * (keyed by trace id). On any trace surface, a trace with one of these names
 * should render the FULL brief (via BriefMessageViewerByTrace) instead of the
 * trace's bare status string ("sent N messages"). A non-persisted brief just
 * 404s and falls back to the Langfuse link.
 */
const BRIEF_TRACE_NAMES: ReadonlySet<string> = new Set([
  'scheduled_brief',
  'mini_brief',
  'onboarding_message',
]);

/** True when a trace is a delivered brief whose content lives in brief_messages. */
export function isBriefTraceName(name: string | null | undefined): boolean {
  return name != null && BRIEF_TRACE_NAMES.has(name);
}

/**
 * Title-case fallback for unknown trace names: split on `-`, `_`, `:`,
 * capitalise each segment, collapse runs of whitespace. Keeps us resilient
 * to new trace names landing in production without first being mapped.
 */
function titleCase(raw: string): string {
  if (!raw) return 'Unknown';
  return raw
    .split(/[-_:]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function kindLabel(name: string | null | undefined): string {
  if (!name) return 'Unknown';
  const exact = KIND_LABELS[name];
  if (exact) return exact;
  // Catch-all for `*_click` event traces we haven't specifically mapped.
  if (name.endsWith('_click')) return 'Link click';
  return titleCase(name);
}
